// src/pages/RequirementsGathering.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import SectionNavigation from '../components/SectionNavigation';
import FeatureSubsection from '../components/FeatureSubsection';
import ProgressTracker from '../components/ProgressTracker';
import FeatureFilter from '../components/FeatureFilter';
import { Bell, FileText, Download } from 'lucide-react';
import socketService from '../services/socketService';
import ActivityModal from '../components/ActivityModal';
import ReportModal from '../components/ReportModal';
import ProgressViewToggle from '../components/ProgressViewToggle';
import { generateReportHTML } from '../utils/reportGenerators';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import debugLogger from '../utils/debug';
import DialogManager from '../components/DialogManager';
import { toast } from 'react-hot-toast';
import { calculateStatistics } from '../utils/statisticsUtils';
import ReportSettingsModal from '../components/ReportSettingsModal';
import ReportLoadingDialog from '../components/ReportLoadingDialog';
import AIAnalysisManager from '../components/AIAnalysisManager';

// Define helper components first
const LoadingDialog = ({ isOpen, message = "Loading..." }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl">
                <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <div className="text-lg">{message}</div>
                </div>
            </div>
        </div>
    );
};

const RequirementsGathering = ({ user, organization }) => {
    // State Management
    const [sections, setSections] = useState([]);
    const [currentSection, setCurrentSection] = useState(null);
    const [features, setFeatures] = useState([]);
    const [responses, setResponses] = useState({});
    const [teamResponses, setTeamResponses] = useState([]);
    const [comments, setComments] = useState({});
    const [reactions, setReactions] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    const [featureFilter, setFeatureFilter] = useState('all');
    const [viewMode, setViewMode] = useState('sections');
    const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
    const [pendingResponse, setPendingResponse] = useState(null);
    const [dependencies, setDependencies] = useState({});
    const [dependents, setDependents] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationType, setConfirmationType] = useState(null);
    const [lastViewedTimestamp, setLastViewedTimestamp] = useState(() => {
        return localStorage.getItem('lastActivityView') || new Date(0).toISOString();
    });
    const [featureDependencies, setFeatureDependencies] = useState({});
    const [dependentFeatures, setDependentFeatures] = useState({});
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportContent, setReportContent] = useState(null);
    const [currentReportId, setCurrentReportId] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isReportReady, setIsReportReady] = useState(false);
    const isNavigating = useRef(false);
    const pendingResponseRef = useRef(null);
    const lastProcessedUpdate = useRef({});
    const [dialogQueue, setDialogQueue] = useState([]);
    const lastRequestId = useRef(null);
    const dependencyUpdateTimeout = useRef(null);
    const [showReportSettings, setShowReportSettings] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [reportHistory, setReportHistory] = useState([]);
    const [showAIAnalysis, setShowAIAnalysis] = useState(false);

    // Dependency Tracking
    const updateDependencyTracking = async (currentResponses = responses) => {
        if (!organization?.id || !currentResponses) {
            debugLogger.debug('DEPENDENCIES', 'No organization ID or responses, skipping dependency tracking');
            return;
        }

        // Clear any pending timeout
        if (dependencyUpdateTimeout.current) {
            clearTimeout(dependencyUpdateTimeout.current);
        }

        // Set a new timeout
        dependencyUpdateTimeout.current = setTimeout(async () => {
            try {
                debugLogger.debug('DEPENDENCIES', 'Starting dependency tracking update', currentResponses);

                const [depsResponse, featuresResponse] = await Promise.all([
                    fetch('/api/features/dependencies/all'),
                    fetch(`/api/features/${organization.id}`)
                ]);

                if (!depsResponse.ok || !featuresResponse.ok) {
                    throw new Error('Failed to fetch dependency data');
                }

                const deps = await depsResponse.json();
                const allFeatures = await featuresResponse.json();

                const featureMap = allFeatures.reduce((acc, feature) => {
                    acc[feature.reference] = feature;
                    return acc;
                }, {});

                const newDependencies = {};
                const newDependents = {};

                // Create a unique key for each dependency to prevent duplicates
                const uniqueDeps = new Map();
                
                deps.forEach(dep => {
                    const key = `${dep.feature_reference}-${dep.required_feature_reference}`;
                    if (!uniqueDeps.has(key)) {
                        uniqueDeps.set(key, {
                            required_feature_reference: dep.required_feature_reference,
                            name: featureMap[dep.required_feature_reference]?.name,
                            is_system_dependency: dep.is_system_dependency === 1,
                            description: featureMap[dep.required_feature_reference]?.description
                        });
                    }

                    // Track dependents
                    if (!newDependents[dep.required_feature_reference]) {
                        newDependents[dep.required_feature_reference] = [];
                    }
                    newDependents[dep.required_feature_reference].push({
                        feature_reference: dep.feature_reference,
                        name: featureMap[dep.feature_reference]?.name,
                        is_system_dependency: dep.is_system_dependency === 1,
                        description: featureMap[dep.feature_reference]?.description
                    });
                });

                // Convert unique dependencies back to the original format
                uniqueDeps.forEach((value, key) => {
                    const [featureRef] = key.split('-');
                    if (!newDependencies[featureRef]) {
                        newDependencies[featureRef] = [];
                    }
                    newDependencies[featureRef].push(value);
                });

                // Update state with deduplicated dependencies
                setDependencies(newDependencies);
                setDependents(newDependents);

                debugLogger.debug('DEPENDENCIES', 'Dependency tracking updated', {
                    dependencies: newDependencies,
                    dependents: newDependents
                });
            } catch (error) {
                console.error('Error updating dependencies:', error);
            }
        }, 500); // Debounce for 500ms
    };

    // Data Loading
    const loadData = async () => {
        if (!organization) return;
        setDataLoading(true);
        setError(null);
        
        // Set navigation flag
        isNavigating.current = true;

        try {
            const [featuresResponse, responsesResponse, commentsResponse, reactionsResponse] =
                await Promise.all([
                    fetch(`/api/features/${organization.id}`),
                    fetch(`/api/responses/${organization.id}`),
                    fetch(`/api/comments/${organization.id}`),
                    fetch(`/api/reactions/${organization.id}`)
                ]);

            if (!featuresResponse.ok || !responsesResponse.ok ||
                !commentsResponse.ok || !reactionsResponse.ok) {
                throw new Error('Failed to fetch data');
            }

            const [featuresData, responsesData, commentsData, reactionsData] =
                await Promise.all([
                    featuresResponse.json(),
                    responsesResponse.json(),
                    commentsResponse.json(),
                    reactionsResponse.json()
                ]);

            // Create sections list
            const uniqueNames = [...new Set(featuresData.map(feature => feature.section))];
            const uniqueSections = uniqueNames.map(section => ({
                id: section.toLowerCase().replace(/\s+/g, '-'),
                name: section
            }));

            // Process responses
            const userResponsesMap = {};
            const filteredTeamResponses = [];

            responsesData.forEach(response => {
                if (response.organization_id === organization.id) {
                    if (response.user_id === user.id) {
                        userResponsesMap[response.feature_reference] = response;
                    }
                    filteredTeamResponses.push(response);
                }
            });

            setSections(uniqueSections);
            setFeatures(featuresData);
            setResponses(userResponsesMap);
            setTeamResponses(filteredTeamResponses);
            setComments(commentsData || {});
            setReactions(reactionsData || []);
            setCurrentSection(uniqueSections[0]?.id);

            // Update dependencies
            await updateDependencyTracking(userResponsesMap);

        } catch (error) {
            console.error('Error loading data:', error);
            setError(error.message);
        } finally {
            setDataLoading(false);
            // Clear navigation flag
            isNavigating.current = false;
        }
    };

    // Socket Connection
    useEffect(() => {
        if (!organization?.id) return;

        const handleResponseUpdate = async (data) => {
            debugLogger.debug('SOCKET_UPDATE', 'Received response update:', {
                data,
                currentUserId: user.id,
                requestId: data.requestId,
                lastRequestId: lastRequestId.current
            });
            
            if (!data.teamResponses) {
                debugLogger.debug('SOCKET_UPDATE', 'No team responses in update');
                return;
            }

            // If this is an old request, ignore it
            if (lastRequestId.current && data.requestId && data.requestId < lastRequestId.current) {
                debugLogger.debug('SOCKET_UPDATE', 'Ignoring old update', {
                    current: lastRequestId.current,
                    received: data.requestId
                });
                return;
            }

            // Update the last request ID
            if (data.requestId) {
                lastRequestId.current = data.requestId;
            }

            // Get the most recent response for this feature from the team responses
            const currentUserResponse = data.teamResponses.find(r => r.user_id === user.id);
            
            // Check if this update matches our current state
            const currentStateResponse = responses[data.feature_reference];
            if (currentUserResponse?.response_value === currentStateResponse?.response_value) {
                debugLogger.debug('SOCKET_UPDATE', 'Update matches current state, skipping');
                return;
            }

            // Process updates atomically
            const updateStates = () => {
                setTeamResponses(prev => {
                    const newTeamResponses = prev.filter(response =>
                        response.feature_reference !== data.feature_reference
                    );
                    return [...newTeamResponses, ...data.teamResponses];
                });

                if (data.userId === user.id) {
                    debugLogger.debug('SOCKET_UPDATE', 'Updating user response:', data.userResponse);
                    setResponses(prev => {
                        const newResponses = { ...prev };
                        if (data.userResponse) {
                            newResponses[data.feature_reference] = data.userResponse;
                        } else {
                            delete newResponses[data.feature_reference];
                        }
                        return newResponses;
                    });
                }
            };

            // Perform the updates
            updateStates();

            // Update dependencies if needed (debounced)
            if (data.userId === user.id) {
                await updateDependencyTracking();
            }
        };

        socketService.connect();
        socketService.joinOrganization(organization.id);
        socketService.onResponseUpdate(handleResponseUpdate);

        socketService.onError((error) => {
            console.error('Socket error:', error);
            setError('Lost connection to server. Please refresh the page.');
        });

        socketService.onReconnect(() => {
            console.log('Socket reconnected, refreshing data...');
            loadData();
        });

        socketService.onCommentUpdate((data) => {
            if (!data.feature_reference || !data.comment) return;

            setComments(previousComments => {
                const featureReference = data.feature_reference;
                const existingComments = previousComments[featureReference] || [];
                const updatedComments = [...existingComments, data.comment];
                return {
                    ...previousComments,
                    [featureReference]: updatedComments.sort((a, b) =>
                        new Date(a.created_at) - new Date(b.created_at))
                };
            });
        });

        socketService.onReactionUpdate((data) => {
            setReactions(previousReactions => {
                if (data.removed) {
                    return previousReactions.filter(reaction =>
                        !(reaction.comment_id === data.comment_id &&
                            reaction.emoji === data.emoji &&
                            reaction.user_id === data.user_id)
                    );
                }
                const exists = previousReactions.some(reaction =>
                    reaction.comment_id === data.comment_id &&
                    reaction.emoji === data.emoji &&
                    reaction.user_id === data.user_id
                );
                if (exists) return previousReactions;
                return [...previousReactions, data];
            });
        });

        return () => {
            socketService.disconnect();
            // Clear any pending timeouts
            if (dependencyUpdateTimeout.current) {
                clearTimeout(dependencyUpdateTimeout.current);
            }
        };
    }, [organization?.id, user.id]);

    // Initial Data Load
    useEffect(() => {
        if (organization?.id) {
            loadData();
        }
    }, [organization?.id]);

    // Activity Modal Timestamp
    useEffect(() => {
        if (!isActivityModalOpen) {
            const newTimestamp = new Date().toISOString();
            localStorage.setItem('lastActivityView', newTimestamp);
            setLastViewedTimestamp(newTimestamp);
        }
    }, [isActivityModalOpen]);

    const fetchQuestionnaireData = async (organizationId) => {
        if (!organizationId) {
            console.error('Missing organization ID for questionnaire data fetch');
            throw new Error('Organization ID is required');
        }
    
        try {
            console.log('[DEBUG] Starting questionnaire data fetch for org:', organizationId);
    
            // Fetch all data in parallel with detailed error handling
            const [questionsResponse, orgResponsesResponse, userResponsesResponse] = await Promise.all([
                fetch('/api/core-questions', {
                    credentials: 'include',
                    headers: { 'Cache-Control': 'no-cache' }
                }).then(async (response) => {
                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error(`Questions fetch failed: ${response.status} - ${text}`);
                    }
                    return response.json();
                }),
    
                fetch(`/api/core-questions/responses/${organizationId}/official`, {
                    credentials: 'include',
                    headers: { 'Cache-Control': 'no-cache' }
                }).then(async (response) => {
                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error(`Official responses fetch failed: ${response.status} - ${text}`);
                    }
                    return response.json();
                }),
    
                fetch(`/api/core-questions/responses/${organizationId}/team`, {
                    credentials: 'include',
                    headers: { 'Cache-Control': 'no-cache' }
                }).then(async (response) => {
                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error(`Team responses fetch failed: ${response.status} - ${text}`);
                    }
                    return response.json();
                })
            ]);
    
            console.log('[DEBUG] Successfully fetched all questionnaire data:', {
                questions: {
                    count: questionsResponse.length,
                    sample: questionsResponse[0]
                },
                orgResponses: {
                    count: orgResponsesResponse.length,
                    sample: orgResponsesResponse[0]
                },
                userResponses: {
                    count: userResponsesResponse.length,
                    sample: userResponsesResponse[0]
                }
            });
    
            // Validate response structures
            if (!Array.isArray(questionsResponse)) {
                throw new Error('Questions response is not an array');
            }
    
            // Transform org responses into expected format
            const orgResponsesMap = orgResponsesResponse.reduce((acc, response) => {
                if (response.question_id) {
                    acc[response.question_id] = {
                        ...response,
                        response_value: response.response_value,
                        question_type: response.question_type,
                        question_options: response.question_options
                    };
                }
                return acc;
            }, {});
    
            console.log('[DEBUG] Processed questionnaire data:', {
                questionCount: questionsResponse.length,
                orgResponseCount: Object.keys(orgResponsesMap).length,
                userResponseCount: userResponsesResponse.length
            });
    
            return {
                questions: questionsResponse,
                orgResponses: orgResponsesMap,
                userResponses: userResponsesResponse
            };
    
        } catch (error) {
            console.error('[ERROR] Questionnaire data fetch failed:', {
                error: error.message,
                stack: error.stack,
                organizationId
            });
            throw error; // Re-throw to be handled by caller
        }
    };

    // Response Change Handler
    const handleResponseChange = async (featureReference, value) => {
        try {
            const requestId = Date.now();
            lastRequestId.current = requestId;

            debugLogger.debug('RESPONSE_CHANGE', 'Starting response change:', {
                featureReference,
                value,
                userId: user.id,
                requestId
            });

            // If setting to required, first check dependencies
            if (value === 'required') {
                const deps = await fetchDependencies(featureReference);
                const systemDeps = deps.filter(dep => dep.is_system_dependency);
                
                if (systemDeps.length > 0) {
                    // Show dependency confirmation dialog
                    setPendingResponse({ featureRef: featureReference, value });
                    pendingResponseRef.current = { featureRef: featureReference, value };
                    setDialogQueue([{
                        type: 'dependency',
                        feature: features.find(f => f.reference === featureReference),
                        dependencies: systemDeps,
                        message: 'Setting this feature as Required will mark the following as system dependencies:'
                    }]);
                    return;
                }
            }

            // Create the new response object
            const newResponse = value === null ? undefined : {
                feature_reference: featureReference,
                response_value: value,
                organization_id: organization.id,
                user_id: user.id,
                user_name: user.name
            };

            debugLogger.debug('RESPONSE_CHANGE', 'Optimistic update with:', newResponse);

            // Batch state updates together
            const batchStateUpdate = () => {
                setResponses(prev => {
                    const newResponses = { ...prev };
                    if (newResponse) {
                        newResponses[featureReference] = newResponse;
                    } else {
                        delete newResponses[featureReference];
                    }
                    return newResponses;
                });

                setTeamResponses(prev => {
                    const otherResponses = prev.filter(response =>
                        response.feature_reference !== featureReference ||
                        response.user_id !== user.id
                    );
                    return newResponse ? [...otherResponses, newResponse] : otherResponses;
                });
            };

            // Perform optimistic update
            batchStateUpdate();

            debugLogger.debug('RESPONSE_CHANGE', 'Making API request');

            const response = await fetch('/api/responses', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feature_reference: featureReference,
                    value,
                    organization_id: organization.id,
                    requestId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update response');
            }

            const result = await response.json();
            debugLogger.debug('RESPONSE_CHANGE', 'API response:', result);

        } catch (error) {
            console.error('Error updating response:', error);
            debugLogger.error('RESPONSE_CHANGE', 'Error occurred:', error);
            
            // Revert both states on error
            setResponses(prev => ({
                ...prev,
                [featureReference]: prev[featureReference]
            }));
            setTeamResponses(prev => [...prev]);
            toast.error(error.message || 'Failed to update response');
        }
    };

    // Comment Handler
    const handleCommentAdd = async (featureReference, commentText, parentId = null) => {
        try {
            const response = await fetch('/api/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feature_reference: featureReference,
                    comment_text: commentText,
                    parent_id: parentId,
                    organization_id: organization.id
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error('Failed to add comment');
        }
    };

    // Reaction Handler
    const handleReactionAdd = async (commentId, emoji) => {
        try {
            const response = await fetch('/api/reactions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    comment_id: commentId,
                    emoji,
                    organization_id: organization.id
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save reaction');
            }
        } catch (error) {
            console.error('Error managing reaction:', error);
            toast.error('Failed to save reaction');
        }
    };

    const fetchDependencies = async (featureRef) => {
        try {
            const response = await fetch(`/api/features/${featureRef}/dependencies`);

            if (!response.ok) {
                throw new Error('Failed to fetch dependencies');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error in fetchDependencies:', error);
            return [];
        }
    };

    // Feature Navigation
    const handleNavigateToFeature = (featureReference) => {
        const feature = features.find(feature => feature.reference === featureReference);
        if (feature) {
            const section = sections.find(section => section.name === feature.section);
            if (section) {
                setCurrentSection(section.id);
            }

            setTimeout(() => {
                const element = document.getElementById(`feature-${featureReference}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.classList.add('bg-yellow-50');
                    setTimeout(() => {
                        element.classList.remove('bg-yellow-50');
                    }, 2000);
                }
            }, 100);
        }
    };

    // Activity Click Handler
    const handleActivityClick = (activity) => {
        setIsActivityModalOpen(false);
        if (activity.feature_reference) {
            handleNavigateToFeature(activity.feature_reference);
        }
    };

    // Report Generation
    const generateFullReport = async (settings) => {
        try {
            setReportLoading(true);
            setError(null);
            setIsReportReady(false);
            setPdfUrl(null);
            setCurrentReportId(null);

            // Validate required data
            if (!organization?.id || !organization?.name) {
                throw new Error('Organization data is missing');
            }

            if (!user?.name || !user?.email) {
                throw new Error('User data is missing');
            }

            if (!Array.isArray(features)) {
                throw new Error('Features data is invalid');
            }

            if (!Array.isArray(teamResponses)) {
                throw new Error('Team responses data is invalid');
            }

            debugLogger.debug('REPORT', 'Starting report generation for org', organization.id);
            
            let questionnaireData = null;
            try {
                questionnaireData = await fetchQuestionnaireData(organization.id);
                debugLogger.debug('REPORT', 'Successfully fetched questionnaire data', {
                    questionCount: questionnaireData?.questions?.length || 0,
                    orgResponseCount: Object.keys(questionnaireData?.orgResponses || {}).length,
                    userResponseCount: questionnaireData?.userResponses?.length || 0
                });
            } catch (questionnaireError) {
                debugLogger.warn('REPORT', 'Failed to fetch questionnaire data', questionnaireError);
                // Don't set error, just log warning since questionnaire is optional
            }
    
            // Safely get user initials
            const userInitials = user.name.split(' ')
                .filter(part => part) // Filter out empty parts
                .map(name => name[0] || '')
                .join('');

            const reportData = {
                title: `Requirements Gathering Report - ${organization.name}`,
                date: new Date().toLocaleDateString(),
                organization: organization,
                settings: {
                    generatedAt: new Date().toISOString(),
                    paperSize: 'A4',
                    featureTypes: ['required', 'nice_to_have', 'not_needed', 'need_more_info'],
                    includeAISummary: true,
                    includeComments: true,
                    includeReactions: true,
                    showUserDetails: true
                },
                users: [
                    {
                        initials: userInitials,
                        name: user.name,
                        email: user.email
                    },
                    ...Object.values((teamResponses || []).reduce((accumulator, response) => {
                        if (response?.user_id && response?.user_name && !accumulator[response.user_id]) {
                            const initials = (response.user_name || '')
                                .split(' ')
                                .filter(part => part)
                                .map(name => name[0] || '')
                                .join('');
                                
                            accumulator[response.user_id] = {
                                initials,
                                name: response.user_name,
                                email: response.email || 'N/A'
                            };
                        }
                        return accumulator;
                    }, {}))
                ],
                features: (features || []).map(feature => {
                    if (!feature?.reference) {
                        debugLogger.warn('REPORT', 'Invalid feature found', { feature });
                        return null;
                    }

                    const featureResponses = (teamResponses || []).filter(response =>
                        response?.feature_reference === feature.reference &&
                        response?.organization_id === organization.id
                    );

                    const userResponse = responses?.[feature.reference];

                    return {
                        ...feature,
                        responses: featureResponses,
                        comments: comments?.[feature.reference] || [],
                        dependencies: featureDependencies?.[feature.reference] || [],
                        conflicts: [],
                        userResponse: userResponse
                    };
                }).filter(Boolean), // Remove any null features
                responses: responses || {},
                statistics: calculateStatistics(features, responses, organization, comments),
                questions: questionnaireData?.questions || [],
                orgResponses: questionnaireData?.orgResponses || {},
                userResponses: questionnaireData?.userResponses || [],
                includeQuestionnaire: Boolean(questionnaireData?.questions?.length)
            };
    
            debugLogger.debug('REPORT', 'Prepared report data', {
                featureCount: reportData.features.length,
                userCount: reportData.users.length,
                includeQuestionnaire: reportData.includeQuestionnaire,
                questionnaireDataPresent: Boolean(questionnaireData)
            });
    
            // Generate the HTML content
            const html = await generateReportHTML(reportData);
            
            // Post to generate PDF
            const response = await fetch('/api/reports/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    organization_id: organization.id,
                    report_type: 'full_report',
                    content_html: html,
                    metadata: {
                        generated_from: window.location.pathname,
                        browser: navigator.userAgent,
                        includesQuestionnaire: reportData.includeQuestionnaire
                    }
                })
            });
    
            if (!response.ok) {
                throw new Error(`Failed to generate PDF report: ${response.statusText}`);
            }
    
            const result = await response.json();
            setCurrentReportId(result.report_id);
            setPdfUrl(result.pdf_url);
            setIsReportReady(true);
            setError(null);
    
            return result;
        } catch (error) {
            debugLogger.error('REPORT', 'Report generation failed', { 
                error: error.message,
                stack: error.stack,
                organizationId: organization?.id
            });
            setError(`Failed to generate report: ${error.message}`);
            setIsReportReady(false);
            return null;
        } finally {
            setReportLoading(false);
        }
    };

    const loadReportHistory = async () => {
        try {
            const response = await fetch(`/api/reports/history/${organization.id}`);
            if (!response.ok) throw new Error('Failed to load report history');
            const history = await response.json();
            setReportHistory(history);
        } catch (error) {
            console.error('Error loading report history:', error);
            toast.error('Failed to load report history');
        }
    };

    const handleGenerateReport = async (settings) => {
        setShowReportSettings(false);
        setIsGenerating(true);
        try {
            const result = await generateFullReport(settings);
            
            if (result) {
                setCurrentReportId(result.report_id);
                setPdfUrl(result.pdf_url);
                setIsReportReady(true);
                await loadReportHistory();
                setIsReportModalOpen(true);
            }
        } catch (error) {
            console.error('Failed to generate report:', error);
            setError('Failed to generate report. Please try again.');
            toast.error('Failed to generate report');
        } finally {
            setIsGenerating(false);
        }
    };

    // Feature Filtering
    const filterFeatures = (featuresToFilter) => {
        return featuresToFilter.filter(feature => {
            if (viewMode === 'sections' &&
                feature.section !== sections.find(section => section.id === currentSection)?.name) {
                return false;
            }

            const userResponse = responses[feature.reference];

            switch (featureFilter) {
                case 'unanswered':
                    return !userResponse || !userResponse.response_value;
                case 'answered':
                    return userResponse && userResponse.response_value;
                case 'dont_know':
                    return userResponse?.response_value === 'dont_know';
                default:
                    return true;
            }
        });
    };

    // Memoized Values
    const currentFeatures = useMemo(() => {
        return features.filter(feature =>
            feature.section === sections.find(section => section.id === currentSection)?.name
        );
    }, [features, sections, currentSection]);

    const filteredFeatures = useMemo(() => {
        return filterFeatures(features);
    }, [features, viewMode, currentSection, featureFilter, responses]);

    const statistics = calculateStatistics(features, responses, organization, comments);

    // Render Component
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Dialog Manager */}
            <DialogManager
                dialogQueue={dialogQueue}
                setDialogQueue={setDialogQueue}
                pendingResponse={pendingResponse}
                setPendingResponse={setPendingResponse}
                pendingResponseRef={pendingResponseRef}
                onResponseChange={handleResponseChange}
                updateDependencyTracking={updateDependencyTracking}
            />

            {/* Report Settings Modal */}
            {showReportSettings && (
                <ReportSettingsModal
                    onClose={() => setShowReportSettings(false)}
                    onGenerate={handleGenerateReport}
                />
            )}

            {/* AI Analysis Modal */}
            {showAIAnalysis && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-semibold">AI Feature Analysis</h2>
                            <button
                                onClick={() => setShowAIAnalysis(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                            <AIAnalysisManager organizationId={organization?.id} />
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Dialog */}
            <ReportLoadingDialog 
                isOpen={isGenerating}
                onComplete={() => setIsGenerating(false)}
                isReportReady={isReportReady}
            />

            {/* Report History Modal */}
            <ReportModal
                isOpen={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
                organization={organization}
                user={user}
                features={features}
                responses={responses}
                teamResponses={teamResponses}
                comments={comments}
                reactions={reactions}
                dependencies={featureDependencies}
                reportHistory={reportHistory}
                onReportHistoryChange={loadReportHistory}
            />

            {dataLoading ? (
                <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                    <div className="text-lg flex items-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        Loading data...
                    </div>
                </div>
            ) : (
                <>
                    {/* Error Display */}
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Header Section */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-900">
                            Requirements Gathering
                        </h1>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setIsActivityModalOpen(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                <Bell className="w-4 h-4" />
                                Activity Log
                            </button>
                            <button
                                onClick={() => setShowReportSettings(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                <FileText className="w-4 h-4" />
                                Full Report
                            </button>
                            <button
                                onClick={() => setShowAIAnalysis(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                AI Analysis
                            </button>
                            <button
                                onClick={() => {
                                    const reportData = {
                                        title: `Recommended Solution - ${organization.name}`,
                                        date: new Date().toLocaleDateString(),
                                        features,
                                        responses,
                                        dependencies: featureDependencies
                                    };
                                    const html = generateRecommendedSolutionHTML(reportData);
                                    const newWindow = window.open('', '_blank');
                                    newWindow.document.write(html);
                                    newWindow.document.close();
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                            >
                                <Download className="w-4 h-4" />
                                Recommended Solution
                            </button>
                        </div>
                    </div>

                    {/* Activity Modal */}
                    {isActivityModalOpen && (
                        <ActivityModal
                            isOpen={isActivityModalOpen}
                            onClose={() => setIsActivityModalOpen(false)}
                            teamResponses={teamResponses}
                            comments={comments}
                            reactions={reactions}
                            features={features}
                            lastViewedTimestamp={lastViewedTimestamp}
                            onActivityClick={handleActivityClick}
                        />
                    )}

                    {/* Main Content Layout */}
                    <div className="lg:flex gap-8">
                        {/* Left Sidebar */}
                        <div className="lg:w-1/3">
                            <ProgressViewToggle
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                            />
                            <div className={`${viewMode === 'all' ? 'opacity-50 pointer-events-none' : ''}`}>
                                <ProgressTracker
                                    sections={sections}
                                    currentSection={currentSection}
                                    responses={responses}
                                    features={features}
                                    onSectionChange={setCurrentSection}
                                    viewMode={viewMode}
                                    organization={organization}
                                    dependencies={dependencies}
                                    dependents={dependents}
                                />
                            </div>
                        </div>

                        {/* Right Content Area */}
                        <div className="lg:w-2/3">
                            <FeatureFilter
                                currentFilter={featureFilter}
                                onFilterChange={setFeatureFilter}
                            />
                            <FeatureSubsection
                                features={filteredFeatures}
                                responses={responses}
                                onResponseChange={handleResponseChange}
                                comments={comments}
                                onCommentAdd={handleCommentAdd}
                                onReactionAdd={handleReactionAdd}
                                reactions={reactions}
                                teamResponses={teamResponses}
                                currentUser={user}
                                organization={organization}
                                showAllSections={viewMode === 'all'}
                                featureDependencies={featureDependencies}
                                dependentFeatures={dependentFeatures}
                                onNavigateToFeature={handleNavigateToFeature}
                                fetchDependencies={fetchDependencies}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default RequirementsGathering;